import { useCallback, useLayoutEffect, useRef } from 'react'
import { LoadingContext } from '@components/Context/LoadingContext'
import React from 'react'

const inProgressSuffix = 'InProgress'

export type UseLoadingContextOptions<TArgs extends unknown[] = []> = {
  /** Async method to execute. Receives args forwarded from triggerFetch. */
  asyncFunction: (...args: TArgs) => Promise<void>
  /** Unique ID for this operation (OperationId + component name). */
  loadingId: string
  /** Optional callback invoked after the async function completes. */
  callback?: () => void
  /**
   * If true, allows a new triggerFetch to start even while one is in-flight.
   * The latest call manages the loading state; earlier completions are ignored.
   * Use ONLY for readonly operations where parameters can change rapidly
   * (e.g., navigation, search-as-you-type).
   * For mutations, keep the default (false) and disable the button instead.
   */
  allowReentry?: boolean
}

export type UseLoadingContext<TArgs extends unknown[] = []> = {
  /** Whether the loading ID is currently present in the LoadingContext set. */
  isLoading: boolean
  /** Executes the async function and manages loading state. */
  triggerFetch: (...args: TArgs) => void
  /** The raw LoadingContext set — for advanced use cases. */
  loadingIds: Set<string>
  /** Add loading IDs to the LoadingContext. */
  addLoadingIds: (state: string[]) => void
  /** Remove loading IDs from the LoadingContext. */
  removeLoadingIds: (state: string[]) => void
}

const useLoadingContext = <TArgs extends unknown[] = []>({
  asyncFunction,
  callback,
  loadingId,
  allowReentry = false,
}: UseLoadingContextOptions<TArgs>): UseLoadingContext<TArgs> => {
  const { loadingIds, addLoadingIds, removeLoadingIds } =
    React.useContext(LoadingContext)

  const inProgressId = `${loadingId}${inProgressSuffix}`
  const asyncFunctionRef = useRef(asyncFunction)
  const callbackRef = useRef(callback)
  const lockRef = useRef(false)
  const latestRequestIdRef = useRef(0)

  /**
   * useLayoutEffect ensures refs are updated before effects (and thus before
   * any triggerFetch call that an effect may fire).
   */
  useLayoutEffect(() => {
    asyncFunctionRef.current = asyncFunction
    callbackRef.current = callback
  }, [asyncFunction, callback])

  /**
   * Locked mode (default):
   *   Prevents re-entry while a request is in-flight.
   *   Every completion cleans up the loading state.
   *   Protects mutations from double-submission.
   */
  const runLocked: (...args: TArgs) => void = useCallback(
    (...args) => {
      if (lockRef.current) return
      lockRef.current = true

      const execute = async () => {
        addLoadingIds([loadingId, inProgressId])
        try {
          await asyncFunctionRef.current(...args)
        } catch {
          // Consumer-level error handling preferred; catch prevents
          // unhandled promise rejections from the async pipeline.
        } finally {
          lockRef.current = false
          removeLoadingIds([loadingId, inProgressId])
          callbackRef.current?.()
        }
      }

      execute()
    },
    [addLoadingIds, removeLoadingIds, loadingId, inProgressId]
  )

  /**
   * Reentrant mode:
   *   Allows concurrent executions. Each call increments a counter.
   *   When a request completes, only the latest call's completion
   *   cleans up the loading state — earlier completions are ignored.
   *   Prevents the loading state from toggling off while a newer
   *   request is still in-flight.
   */
  const runReentrant: (...args: TArgs) => void = useCallback(
    (...args) => {
      const requestId = ++latestRequestIdRef.current

      const execute = async () => {
        addLoadingIds([loadingId, inProgressId])
        try {
          await asyncFunctionRef.current(...args)
        } catch {
          // Consumer-level error handling preferred; catch prevents
          // unhandled promise rejections from the async pipeline.
        } finally {
          if (requestId === latestRequestIdRef.current) {
            removeLoadingIds([loadingId, inProgressId])
            callbackRef.current?.()
          }
        }
      }

      execute()
    },
    [addLoadingIds, removeLoadingIds, loadingId, inProgressId]
  )

  const triggerFetch = allowReentry ? runReentrant : runLocked

  const isLoading = loadingIds?.has?.(loadingId) || false

  return {
    isLoading,
    triggerFetch,
    loadingIds,
    addLoadingIds,
    removeLoadingIds,
  }
}

export default useLoadingContext
